import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import {
  DugnutStatsDataDti,
  ExamDto,
  ParamStatsGetAllPrescriber,
  SiteDto,
  TableStatsPrescriberDti,
  TableStatsUserDti,
} from '@services/api'
import {
  GetExamStatsQuery,
  StatsExamTypeCodeBreakdownDTO,
} from '@services/examStatsService'
import {
  GetPatientStatsQuery,
  StatsPatientAgeSummaryDTO,
  StatsPatientCareTypeBreakdownDTO,
  StatsPatientGenderBreakdownDTO,
  StatsPatientOriginByDepartmentDTO,
  StatsPatientOriginByMunicipalityDTO,
} from '@services/patientStatsService'
import {
  GetReferringPhysicianStatsQuery,
  StatsReferringPhysicianDepartmentBreakdownDTO,
  StatsReferringPhysicianPostalCodeBreakdownDTO,
  StatsReferringPhysicianSpecialtyBreakdownDTO,
} from '@services/referringPhysicianStatsService'
import {
  getAllPrescriber,
  getAllTopEvent,
  getExamBreakdown,
  getExamStats,
  getExamStatsOptions,
  getPatientStats,
  getPatientStatsOptions,
  getReferringPhysicianStats,
  getReferringPhysicianStatsOptions,
} from '@state/thunks/statsThunk'

// interface FilterChangePeriodPayload {
//   startY: number;
//   endY: number;
// }

const formatDateForInput = (date: Date) => {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')

  return `${year}-${month}-${day}`
}

const getDefaultExamStatsFilters = (): GetExamStatsQuery => {
  const today = new Date()
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

  return {
    startDate: formatDateForInput(startOfMonth),
    endDate: formatDateForInput(today),
    siteId: undefined,
    examId: undefined,
  }
}

const getDefaultPatientStatsFilters = (): GetPatientStatsQuery => {
  const today = new Date()
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

  return {
    startDate: formatDateForInput(startOfMonth),
    endDate: formatDateForInput(today),
    siteId: undefined,
    departmentCode: undefined,
    zipCode: undefined,
  }
}

const getDefaultReferringPhysicianStatsFilters =
  (): GetReferringPhysicianStatsQuery => ({
    siteId: undefined,
    departmentCode: undefined,
    zipCode: undefined,
  })

type ExamStatsState = {
  filters: GetExamStatsQuery
  sites: SiteDto[]
  exams: ExamDto[]
  total: number
  breakdown: StatsExamTypeCodeBreakdownDTO[]
  loading: boolean
  loadingOptions: boolean
  hasLoaded: boolean
  hasLoadedOptions: boolean
  error: string | null
}

const createInitialExamStatsState = (): ExamStatsState => ({
  filters: getDefaultExamStatsFilters(),
  sites: [],
  exams: [],
  total: 0,
  breakdown: [],
  loading: false,
  loadingOptions: false,
  hasLoaded: false,
  hasLoadedOptions: false,
  error: null,
})

type PatientStatsState = {
  filters: GetPatientStatsQuery
  sites: SiteDto[]
  total: number
  departments: StatsPatientOriginByDepartmentDTO[]
  municipalities: StatsPatientOriginByMunicipalityDTO[]
  careTypes: StatsPatientCareTypeBreakdownDTO[]
  genders: StatsPatientGenderBreakdownDTO[]
  ageSummary: StatsPatientAgeSummaryDTO | null
  loading: boolean
  loadingOptions: boolean
  hasLoaded: boolean
  hasLoadedOptions: boolean
  error: string | null
}

const createInitialPatientStatsState = (): PatientStatsState => ({
  filters: getDefaultPatientStatsFilters(),
  sites: [],
  total: 0,
  departments: [],
  municipalities: [],
  careTypes: [],
  genders: [],
  ageSummary: null,
  loading: false,
  loadingOptions: false,
  hasLoaded: false,
  hasLoadedOptions: false,
  error: null,
})

type ReferringPhysicianStatsState = {
  filters: GetReferringPhysicianStatsQuery
  sites: SiteDto[]
  total: number
  departments: StatsReferringPhysicianDepartmentBreakdownDTO[]
  postalCodes: StatsReferringPhysicianPostalCodeBreakdownDTO[]
  specialties: StatsReferringPhysicianSpecialtyBreakdownDTO[]
  loading: boolean
  loadingOptions: boolean
  hasLoaded: boolean
  hasLoadedOptions: boolean
  error: string | null
}

const createInitialReferringPhysicianStatsState =
  (): ReferringPhysicianStatsState => ({
    filters: getDefaultReferringPhysicianStatsFilters(),
    sites: [],
    total: 0,
    departments: [],
    postalCodes: [],
    specialties: [],
    loading: false,
    loadingOptions: false,
    hasLoaded: false,
    hasLoadedOptions: false,
    error: null,
  })

type statsState = {
  filter: {
    startY: number
    endY: number
    accountType: string
    eventType: string
    siteId?: number
  }
  tablePrescriber: TableStatsPrescriberDti[],
  tableUser: TableStatsUserDti[],
  examBreakdown: DugnutStatsDataDti,
  examStats: ExamStatsState,
  patientStats: PatientStatsState,
  referringPhysicianStats: ReferringPhysicianStatsState,
}

const initialState: statsState = {
  filter: {
    startY: new Date().getFullYear(),
    endY: new Date().getFullYear(),
    accountType: 'doctor',
    eventType: 'all',
  },
  tablePrescriber: [],
  tableUser: [],
  examBreakdown: {} as DugnutStatsDataDti,
  examStats: createInitialExamStatsState(),
  patientStats: createInitialPatientStatsState(),
  referringPhysicianStats: createInitialReferringPhysicianStatsState(),
}

const statsSlice = createSlice({
  name: 'stats',
  initialState,
  reducers: {
    removeAllStats: (state) => {
      state.tablePrescriber = initialState.tablePrescriber
      state.tableUser = initialState.tableUser
      state.examBreakdown = initialState.examBreakdown
      state.filter = initialState.filter
      state.examStats = createInitialExamStatsState()
      state.patientStats = createInitialPatientStatsState()
      state.referringPhysicianStats =
        createInitialReferringPhysicianStatsState()
    },
    removeStats: (state, action: PayloadAction<string>) => {
      const name = action.payload
      switch (name) {
        case 'prescriber':
          state.tablePrescriber = initialState.tablePrescriber
          break
        case 'user':
          state.tableUser = initialState.tableUser
          break
        case 'exam':
          state.examBreakdown = initialState.examBreakdown
          break
        case 'filter':
          state.filter = initialState.filter
          break
        case 'site':
          state.filter.siteId = initialState.filter.siteId
          break
        case 'exam-stats':
          state.examStats = createInitialExamStatsState()
          break
        case 'patient-stats':
          state.patientStats = createInitialPatientStatsState()
          break
        case 'referring-physician-stats':
          state.referringPhysicianStats =
            createInitialReferringPhysicianStatsState()
          break
        default:
          break
      }
    },
    filterChangePeriod: (state, { payload }: PayloadAction<number>) => {
      state.filter.startY = initialState.filter.startY
      state.filter.startY = payload
    },
    filterChangeSite: (state, { payload }: PayloadAction<number>) => {
      state.filter.siteId = initialState.filter.siteId
      state.filter.siteId = payload
    },
    filterChangeAccountType: (state, { payload }: PayloadAction<string>) => {
      state.filter.accountType = initialState.filter.accountType
      state.filter.accountType = payload
    },
    filterChangeEventType: (state, { payload }: PayloadAction<string>) => {
      state.filter.eventType = initialState.filter.eventType
      state.filter.eventType = payload
    },
    setExamStatsFilters: (
      state,
      { payload }: PayloadAction<Partial<GetExamStatsQuery>>,
    ) => {
      state.examStats.filters = {
        ...state.examStats.filters,
        ...payload,
      }
    },
    resetExamStatsFilters: (state) => {
      state.examStats.filters = getDefaultExamStatsFilters()
      state.examStats.error = null
    },
    clearExamStatsError: (state) => {
      state.examStats.error = null
    },
    setPatientStatsFilters: (
      state,
      { payload }: PayloadAction<Partial<GetPatientStatsQuery>>,
    ) => {
      state.patientStats.filters = {
        ...state.patientStats.filters,
        ...payload,
      }
    },
    resetPatientStatsFilters: (state) => {
      state.patientStats.filters = getDefaultPatientStatsFilters()
      state.patientStats.error = null
    },
    clearPatientStatsError: (state) => {
      state.patientStats.error = null
    },
    setReferringPhysicianStatsFilters: (
      state,
      { payload }: PayloadAction<Partial<GetReferringPhysicianStatsQuery>>,
    ) => {
      state.referringPhysicianStats.filters = {
        ...state.referringPhysicianStats.filters,
        ...payload,
      }
    },
    resetReferringPhysicianStatsFilters: (state) => {
      state.referringPhysicianStats.filters =
        getDefaultReferringPhysicianStatsFilters()
      state.referringPhysicianStats.error = null
    },
    clearReferringPhysicianStatsError: (state) => {
      state.referringPhysicianStats.error = null
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(getAllPrescriber.fulfilled, (state, { payload }: PayloadAction<ParamStatsGetAllPrescriber[] | any>) => {
        state.tablePrescriber = payload
      })
      .addCase(getAllTopEvent.fulfilled, (state, { payload }: PayloadAction<TableStatsUserDti[] | any>) => {
        state.tableUser = payload
      })
      .addCase(getExamBreakdown.fulfilled, (state, { payload }: PayloadAction<DugnutStatsDataDti | any>) => {
        state.examBreakdown = payload
      })
      .addCase(getExamStatsOptions.pending, (state) => {
        state.examStats.loadingOptions = true
        state.examStats.error = null
      })
      .addCase(
        getExamStatsOptions.fulfilled,
        (
          state,
          { payload }: PayloadAction<{ sites: SiteDto[]; exams: ExamDto[] }>,
        ) => {
          state.examStats.loadingOptions = false
          state.examStats.hasLoadedOptions = true
          state.examStats.sites = payload.sites
          state.examStats.exams = payload.exams
        },
      )
      .addCase(getExamStatsOptions.rejected, (state, action) => {
        state.examStats.loadingOptions = false
        state.examStats.error =
          (action.payload as string) ??
          action.error.message ??
          'Impossible de charger les statistiques'
      })
      .addCase(getExamStats.pending, (state) => {
        state.examStats.loading = true
        state.examStats.error = null
      })
      .addCase(
        getExamStats.fulfilled,
        (
          state,
          {
            payload,
          }: PayloadAction<{
            total: number
            breakdown: StatsExamTypeCodeBreakdownDTO[]
          }>,
        ) => {
          state.examStats.loading = false
          state.examStats.hasLoaded = true
          state.examStats.total = payload.total
          state.examStats.breakdown = payload.breakdown
        },
      )
      .addCase(getExamStats.rejected, (state, action) => {
        state.examStats.loading = false
        state.examStats.error =
          (action.payload as string) ??
          action.error.message ??
          'Impossible de charger les statistiques'
      })
      .addCase(getPatientStatsOptions.pending, (state) => {
        state.patientStats.loadingOptions = true
        state.patientStats.error = null
      })
      .addCase(
        getPatientStatsOptions.fulfilled,
        (state, { payload }: PayloadAction<{ sites: SiteDto[] }>) => {
          state.patientStats.loadingOptions = false
          state.patientStats.hasLoadedOptions = true
          state.patientStats.sites = payload.sites
        },
      )
      .addCase(getPatientStatsOptions.rejected, (state, action) => {
        state.patientStats.loadingOptions = false
        state.patientStats.error =
          (action.payload as string) ??
          action.error.message ??
          'Impossible de charger les statistiques'
      })
      .addCase(getPatientStats.pending, (state) => {
        state.patientStats.loading = true
        state.patientStats.error = null
      })
      .addCase(
        getPatientStats.fulfilled,
        (
          state,
          {
            payload,
          }: PayloadAction<{
            total: number
            departments: StatsPatientOriginByDepartmentDTO[]
            municipalities: StatsPatientOriginByMunicipalityDTO[]
            careTypes: StatsPatientCareTypeBreakdownDTO[]
            genders: StatsPatientGenderBreakdownDTO[]
            ageSummary: StatsPatientAgeSummaryDTO
          }>,
        ) => {
          state.patientStats.loading = false
          state.patientStats.hasLoaded = true
          state.patientStats.total = payload.total
          state.patientStats.departments = payload.departments
          state.patientStats.municipalities = payload.municipalities
          state.patientStats.careTypes = payload.careTypes
          state.patientStats.genders = payload.genders
          state.patientStats.ageSummary = payload.ageSummary
        },
      )
      .addCase(getPatientStats.rejected, (state, action) => {
        state.patientStats.loading = false
        state.patientStats.error =
          (action.payload as string) ??
          action.error.message ??
          'Impossible de charger les statistiques'
      })
      .addCase(getReferringPhysicianStatsOptions.pending, (state) => {
        state.referringPhysicianStats.loadingOptions = true
        state.referringPhysicianStats.error = null
      })
      .addCase(
        getReferringPhysicianStatsOptions.fulfilled,
        (state, { payload }: PayloadAction<{ sites: SiteDto[] }>) => {
          state.referringPhysicianStats.loadingOptions = false
          state.referringPhysicianStats.hasLoadedOptions = true
          state.referringPhysicianStats.sites = payload.sites
        },
      )
      .addCase(getReferringPhysicianStatsOptions.rejected, (state, action) => {
        state.referringPhysicianStats.loadingOptions = false
        state.referringPhysicianStats.error =
          (action.payload as string) ??
          action.error.message ??
          'Impossible de charger les statistiques'
      })
      .addCase(getReferringPhysicianStats.pending, (state) => {
        state.referringPhysicianStats.loading = true
        state.referringPhysicianStats.error = null
      })
      .addCase(
        getReferringPhysicianStats.fulfilled,
        (
          state,
          {
            payload,
          }: PayloadAction<{
            total: number
            departments: StatsReferringPhysicianDepartmentBreakdownDTO[]
            postalCodes: StatsReferringPhysicianPostalCodeBreakdownDTO[]
            specialties: StatsReferringPhysicianSpecialtyBreakdownDTO[]
          }>,
        ) => {
          state.referringPhysicianStats.loading = false
          state.referringPhysicianStats.hasLoaded = true
          state.referringPhysicianStats.total = payload.total
          state.referringPhysicianStats.departments = payload.departments
          state.referringPhysicianStats.postalCodes = payload.postalCodes
          state.referringPhysicianStats.specialties = payload.specialties
        },
      )
      .addCase(getReferringPhysicianStats.rejected, (state, action) => {
        state.referringPhysicianStats.loading = false
        state.referringPhysicianStats.error =
          (action.payload as string) ??
          action.error.message ??
          'Impossible de charger les statistiques'
      })
      
})

export const {
  removeStats,
  removeAllStats,
  filterChangePeriod,
  filterChangeSite,
  filterChangeAccountType,
  filterChangeEventType,
  setExamStatsFilters,
  resetExamStatsFilters,
  clearExamStatsError,
  setPatientStatsFilters,
  resetPatientStatsFilters,
  clearPatientStatsError,
  setReferringPhysicianStatsFilters,
  resetReferringPhysicianStatsFilters,
  clearReferringPhysicianStatsError,
} = statsSlice.actions

export default statsSlice.reducer
